import { feedbackForPlayer, selectLinkedPlayer } from "./playerContext";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { HelpLink } from "@/features/docs/HelpLink";
import { FeedbackTabStrip } from "./FeedbackTabStrip";
import { LinkedPlayersStrip } from "./components/LinkedPlayersStrip";
import { FeedbackOverviewTab } from "./FeedbackOverviewTab";
import { FeedbackReviewTab } from "./FeedbackReviewTab";
import { FeedbackHistoryTab } from "./FeedbackHistoryTab";
import { FeedbackGoalsTab } from "./FeedbackGoalsTab";
import {
  FEEDBACK_TAB_LABELS,
  type FeedbackTab,
  normalizeFeedbackTab,
} from "./feedbackTabs";
import {
  reviewedClipIds as deriveReviewedClipIds,
  reviewedNoteIds as deriveReviewedNoteIds,
  reviewedPlaylistIds as deriveReviewedPlaylistIds,
  reviewedSummaryIds as deriveReviewedSummaryIds,
} from "./feedbackHelpers";
import { useMyFeedback } from "./hooks/useMyFeedback";
import { TeamScopeGate } from "@/features/navigation/TeamScopeGate";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { Inbox } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Player/guardian feedback shell — the host for the `/feedback`
 * viewer surfaces.
 *
 * PR 1 of the Feedback Player UX redesign: the four action tabs
 * (Overview · Review · Goals · History) replace the legacy object
 * tabs. The router still accepts the five legacy `?tab=` values
 * (`playlists`, `notes`, `clips`, `summaries`, `development`) and the
 * shell folds them via `normalizeFeedbackTab`; no URL rewrite, so
 * back-button history stays stable.
 *
 * `useMe()` reconciles the auth + scope stores on a direct nav —
 * `/api/my-feedback` resolves the viewer's team from the request
 * scope, so the scope store must be populated first.
 *
 * `useMyFeedback()` is the single mount-time bundle fetch feeding
 * every tab. A 401 / missing token routes to `/login?next=...`.
 *
 * PR 2 ships the unified Review feed + filter chips; PR 3 reshapes
 * the Goals page (action-plan-first via `FeedbackGoalsTab`, the
 * viewer-mode wrapper around the shared development data); PR 5
 * upgrades the History wrapper into a grouped archive.
 */

export function FeedbackShell() {
  // Reconcile auth + scope — `/api/my-feedback` is scope-resolved
  // server-side, so a direct nav must not leave the scope store
  // unpopulated.
  useMe();

  const navigate = useNavigate({ from: "/feedback" });
  const search = useSearch({ from: "/feedback" });
  // Action-first default: bare `/feedback` ⇒ Overview. Legacy
  // `?tab=` values fold to the right action tab.
  const activeTab: FeedbackTab = normalizeFeedbackTab(search.tab);

  const authMe = useAuthStore((s) => s.me);
  const canOpenFeedback = getPublicNavCapabilities(authMe).canOpenFeedback;
  const signedInWithoutFeedbackAccess = !!authMe?.user && !canOpenFeedback;
  const feedback = useMyFeedback(!signedInWithoutFeedbackAccess);
  // Multi-team users must pick a team before the feedback tabs render.
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const activeRole = useScopeStore((s) => s.active?.effective_role);
  const activeTeamName = useScopeStore((s) => s.active?.team.name);
  const activeSeasonName = useScopeStore((s) => s.active?.season?.name);
  const isPlayerView = activeRole === "player";
  const selectedPlayerId = selectLinkedPlayer(feedback.data, search);
  const bundle = useMemo(
    () => feedbackForPlayer(feedback.data, selectedPlayerId),
    [feedback.data, selectedPlayerId],
  );
  const headingPlayer = bundle?.players.find(
    (player) => player.id === selectedPlayerId,
  ) ?? bundle?.players[0];
  const headingFirstName = headingPlayer?.display_name
    ?.trim()
    .split(/\s+/)[0];
  useEffect(() => {
    if (selectedPlayerId && search.player !== selectedPlayerId) {
      void navigate({
        search: (previous) => ({ ...previous, player: selectedPlayerId }),
        replace: true,
      });
    }
  }, [selectedPlayerId, search.player, navigate]);

  // The viewer's reviewed-id sets, derived once from `bundle.reviews`
  // — drive the Reviewed/New pills on the Notes + Playlists cards
  // AND the Overview's "what's new" filtering.
  const reviewedNoteIds = useMemo(
    () => deriveReviewedNoteIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  const reviewedPlaylistIds = useMemo(
    () => deriveReviewedPlaylistIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  // PR 7: clips can carry review rows now. Threaded through to the
  // player-facing surfaces so clip cards and clip modals show the right
  // Mark done / Done ✓ affordance.
  const reviewedClipIds = useMemo(
    () => deriveReviewedClipIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );
  // Phase B: match recaps are markable. Threaded to the Review +
  // History tabs so recap cards show Mark read / Read ✓ and age out
  // of the New band once read.
  const reviewedSummaryIds = useMemo(
    () => deriveReviewedSummaryIds(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  // M9a A8 — auth gate. A 401 from /api/my-feedback redirects to
  // `/login?next=<current path>` (replaces the M4 <InlineLoginPanel>
  // that used to render inline). Kratos sessions are cookie-backed, so
  // the protected API response is the source of truth rather than the
  // legacy localStorage bearer token.
  const isAuthError =
    feedback.error instanceof ApiError && feedback.error.status === 401;
  const showLogin = isAuthError;

  // Guard against re-fire after the redirect changes the URL —
  // see AdminShell for the full explanation.
  //
  // `next` captures the FULL path + search (`/feedback?tab=goals`)
  // at mount via a ref so a viewer deep-linked to a specific tab
  // lands back on that tab after signing in.
  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` || "/feedback"
      : "/feedback",
  );
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current },
      replace: true,
    });
  }, [showLogin, navigate]);

  const roleRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin) return;
    if (!signedInWithoutFeedbackAccess) return;
    if (roleRedirectFired.current) return;
    roleRedirectFired.current = true;
    void navigate({ to: "/hub", replace: true });
  }, [showLogin, signedInWithoutFeedbackAccess, navigate]);

  if (signedInWithoutFeedbackAccess) {
    return (
      <main
        className="min-h-screen p-4 text-ink sm:p-6"
        data-testid="feedback-shell"
      >
        <p className="font-mono text-sm text-ink-muted">Opening your hub…</p>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen p-4 text-ink sm:p-6"
      data-testid="feedback-shell"
    >
      <header className="family-header mb-6">
        <div className="min-w-0">
          <p className="eyebrow mb-1">
            {isPlayerView ? "Your coaching space" : "Player & family"}
          </p>
          <h1 className="flex items-center gap-1.5 text-2xl font-display tracking-display sm:text-3xl">
            {isPlayerView
              ? "My feedback."
              : headingFirstName
              ? `${headingFirstName}’s feedback.`
              : "My Feedback"}
            <HelpLink />
          </h1>
          <p className="text-ink-muted text-sm mt-1">
            {isPlayerView
              ? "Small details from your coach. Something useful for your next game."
              : headingPlayer?.display_name
              ? `Coach feedback and goals for ${headingPlayer.display_name}.`
              : "Coach feedback and goals."}
          </p>
        </div>
        {!showLogin && !selectionRequired ? (
          <div
            className="family-player-context"
            data-player-layout={
              (feedback.data?.players.length ?? 0) === 1
                ? "single"
                : "multiple"
            }
          >
            <p className="eyebrow mb-2">
              {isPlayerView ? "Your player profile" : "Viewing player"}
            </p>
            <LinkedPlayersStrip
              players={feedback.data?.players ?? []}
              photoRequests={feedback.data?.player_photo_requests ?? []}
              selectedPlayerId={selectedPlayerId}
              onSelect={
                isPlayerView
                  ? undefined
                  : (player) =>
                      void navigate({
                        search: (previous) => ({
                          ...previous,
                          player,
                          note: undefined,
                          clip: undefined,
                          summary: undefined,
                          goal: undefined,
                        }),
                      })
              }
            />
            {activeTeamName && activeSeasonName ? (
              <p className="family-player-team text-ink-muted">
                {activeTeamName} · {activeSeasonName}
              </p>
            ) : null}
          </div>
        ) : null}
      </header>

      {showLogin ? null : selectionRequired ? (
        <TeamScopeGate />
      ) : (
        <>
          {/* Linked-players chip strip — who's linked to this
           * account (empty ⇒ "ask a coach"). */}

          {/* The sub-tab strip leads the tab content. */}
          <div className="mb-4">
            <FeedbackTabStrip active={activeTab} />
          </div>

          <section
            key={selectedPlayerId ?? "no-player"}
            aria-label={FEEDBACK_TAB_LABELS[activeTab]}
            data-testid={`feedback-panel-${activeTab}`}
          >
            {feedback.isError && !isAuthError ? (
              <FeedbackErrorState onRetry={() => void feedback.refetch()} />
            ) : feedback.isLoading ? (
              <FeedbackLoadingState />
            ) : activeTab === "overview" ? (
              <FeedbackOverviewTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedClipIds={reviewedClipIds}
              />
            ) : activeTab === "review" ? (
              <FeedbackReviewTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedPlaylistIds={reviewedPlaylistIds}
                /* PR 7: clip review state — drives the Mark done /
                 * Done ✓ affordance on clip cards. Empty for
                 * pre-PR 7 bundles, which keeps clips `new`. */
                reviewedClipIds={reviewedClipIds}
                /* Phase B: recap read state — drives Mark read /
                 * Read ✓ on summary cards. */
                reviewedSummaryIds={reviewedSummaryIds}
                /* PR 2: `?summary=<id>` deep-links to a specific
                 * recap. The Overview's "Read full recap →" /
                 * "Read summary" actions pass this so the Review
                 * tab auto-applies the Summaries filter + expands
                 * the matching card on mount. */
                initialSummaryId={search.summary ?? null}
                /* Phase 2.2: the bell now links to the ITEM, not just
                 * the tab, so a player who taps a notification lands
                 * on the thing that notified them instead of hunting
                 * for it in the feed. Applied once per distinct value
                 * (see `useDeepLinkTarget`) so a background refetch
                 * can't yank a reader back to it later. */
                deepLinkNoteId={search.note ?? null}
                deepLinkClipId={search.clip ?? null}
              />
            ) : activeTab === "goals" ? (
              <FeedbackGoalsTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedPlaylistIds={reviewedPlaylistIds}
                reviewedClipIds={reviewedClipIds}
              />
            ) : (
              <FeedbackHistoryTab
                bundle={bundle}
                reviewedNoteIds={reviewedNoteIds}
                reviewedClipIds={reviewedClipIds}
                reviewedSummaryIds={reviewedSummaryIds}
              />
            )}
          </section>
        </>
      )}
    </main>
  );
}

function FeedbackLoadingState() {
  return (
    <div
      className="family-feedback-state family-feedback-loading"
      role="status"
      aria-label="Loading"
      data-testid="feedback-loading"
    >
      <p className="eyebrow">Loading your team…</p>
      <span className="family-feedback-skeleton family-feedback-skeleton-wide" />
      <span className="family-feedback-skeleton family-feedback-skeleton-short" />
      <span className="family-feedback-skeleton family-feedback-skeleton-panel" />
    </div>
  );
}

function FeedbackErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      className="family-feedback-state family-feedback-error"
      data-testid="feedback-bundle-error"
    >
      <span className="family-feedback-state-icon" aria-hidden="true">
        <Inbox size={26} strokeWidth={1.8} />
      </span>
      <h2>We couldn’t load this right now.</h2>
      <p>Your work is still here. Try loading this view again.</p>
      <Button type="button" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
